// src/pages/RezeptPage.js
import React, { useState, useEffect } from "react";
import "./RezeptPage.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

// 🔥 Firebase
import {
  getCurrentIdToken,
} from "../services/authDataService";
import { saveRecipeData } from "../services/recipeDataService";

import GateIcon from "../components/GateIcon";
import SpeechIcon from "../components/SpeechIcon";


function RezeptPage() {
  const navigate = useNavigate();
const { user } = useAuth();

const myUid = user?.uid || null;
const isVerified = user?.emailVerified === true;
  const [ingredients, setIngredients] = useState("");
  const [recipe, setRecipe] = useState("");
  const [showRecipeOverlay, setShowRecipeOverlay] = useState(false);
  const [savedPopup, setSavedPopup] = useState(false);
  const [hideSavedPopup, setHideSavedPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recipeIsError, setRecipeIsError] = useState(false);

  // 🔐 Login-Pflicht-Popup
  const [showLoginRequiredModal, setShowLoginRequiredModal] = useState(false);

  // ✅ Variante A: Nur im RAM (Refresh -> weg)
  const [lastRecipeSnippet, setLastRecipeSnippet] = useState("");
  const [attemptCount, setAttemptCount] = useState(0); // 0 = noch nichts generiert, 1 = erstes Rezept, 2+ = anderes Rezept

  // ESC zum Schließen des Rezepts
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setShowRecipeOverlay(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  // Info-Popup „nicht mehr anzeigen“
  useEffect(() => {
    const hide = localStorage.getItem("hideRecipeSavedPopup");
    if (hide === "true") setHideSavedPopup(true);
  }, []);

  const cleanRecipe = (text) => {
    return (text || "")
      .toString()
      .split("\n")
      .map((line) => line.replace(/^#+\s*/, "")) // entfernt Markdown-Header wie ### Zutaten
      .join("\n")
      .trim();
  };

  /**
   * ✅ macht den Output "luftig":
   * - sorgt für Leerzeilen zwischen Sektionen
   * - macht Zutaten besser lesbar (Komma-Liste -> einzelne Zeilen)
   * - lässt nummerierte Schritte in Ruhe
   */
  const prettifyRecipeText = (raw) => {
    let text = (raw || "").toString().replace(/\r\n/g, "\n").trim();

    // Normalisiere mehrfach-Leerzeilen
    text = text.replace(/\n{3,}/g, "\n\n");

    const lines = text.split("\n").map((l) => l.trim());

    const isHeader = (l) => /^🍽️|^⏱️|^🛒|^👩‍🍳|^🌿|^🥗/.test(l);

    const ensureBlankLineBefore = (arr, idx) => {
      if (idx <= 0) return;
      if (arr[idx - 1] !== "") arr.splice(idx, 0, "");
    };

    // 1) Leerzeile vor jeden Header (außer erster Zeile)
    for (let i = 0; i < lines.length; i++) {
      if (isHeader(lines[i]) && i > 0) {
        ensureBlankLineBefore(lines, i);
        i++;
      }
    }

    // 2) Zutaten hübscher: Komma-Liste -> Bullet-Lines
    const out = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      out.push(line);

      if (line.startsWith("🛒")) {
        const ingredientLines = [];
        let j = i + 1;

        while (j < lines.length && !isHeader(lines[j])) {
          if (lines[j]) ingredientLines.push(lines[j]);
          j++;
        }

        if (ingredientLines.length === 1 && ingredientLines[0].includes(",")) {
          const parts = ingredientLines[0]
            .split(",")
            .map((p) => p.trim())
            .filter(Boolean);

          out.pop();
          out.push("🛒 Zutaten");
          out.push("");

          parts.forEach((p) => out.push("• " + p));

          i = j - 1;
        } else if (ingredientLines.length > 0) {
          if (out[out.length - 1] === "🛒 Zutaten") out.push("");
        }
      }
    }

    let result = out.join("\n");
    result = result.replace(/\n{3,}/g, "\n\n").trim();

    return result;
  };

  // ✅ Snippet aus dem fertigen Rezept ziehen (für "anderes Rezept")
  const buildSnippet = (fullText) => {
    const t = (fullText || "").toString().trim();
    if (!t) return "";

    // Nimm nicht alles -> nur ein kurzer, modellfreundlicher Kontext (ca. 350-700 Zeichen)
    const maxLen = 650;
    const snippet = t.length > maxLen ? t.slice(0, maxLen).trim() : t;

    // Falls es keine Zeilenumbrüche hat, ist es egal – Snippet ist nur Kontext
    return snippet;
  };

  // 🔐 Klick auf „Meine Rezepte“ (Icon oben rechts)
  const handleMeineRezepteClick = async () => {
    if (!myUid || !isVerified) {
      setShowLoginRequiredModal(true);
      return;
    }
    navigate("/meine-rezepte");
  };

  /**
   * ✅ Backend-ready (Firebase Function + Gemini)
   * ✅ "Anderes Rezept": sendet last snippet + Variation-Seed mit
   */
  const fetchRecipe = async () => {
    if (!myUid || !isVerified) {
      setShowLoginRequiredModal(true);
      return;
    }

    if (!ingredients.trim()) return;

    setIsLoading(true);
    setRecipe("");

    const trimmedIngredients = ingredients.trim();

    // “Rezeptwunsch”-Erkennung
    const isRezeptwunsch =
      trimmedIngredients.toLowerCase().includes("rezept") || trimmedIngredients.length < 5;

    const userIntent = isRezeptwunsch
      ? `Ich möchte ein konkretes Rezept für: "${trimmedIngredients}".`
      : `Ich habe folgende Zutaten: "${trimmedIngredients}". Bitte schlage mir etwas Leckeres vor, das ich daraus kochen kann.`;

    // ✅ Wenn der User "Anderes Rezept" will, geben wir Kontext + klare Anweisung
    const isRetry = attemptCount >= 1 && lastRecipeSnippet;

    // Variation Seed: sorgt auch ohne echten "Memory" dafür, dass es anders wird
    // (klein halten, damit Token nicht explodieren)
    const seed = Date.now().toString().slice(-5); // z.B. 08371
    const variationHint = isRetry
      ? `WICHTIG: Das vorherige Rezept gefiel nicht. Erstelle eine DEUTLICH ANDERE Variante (anderer Titel, andere Zubereitungslogik, anderer Stil z.B. Bowl/Ofen/Pfanne/Suppe/Salat), aber nutze weiterhin die Zutaten. Seed=${seed}`
      : "";

    const previousSnippetBlock = isRetry
      ? `VORHERIGES REZEPT (Snippet, zur Abgrenzung):\n${lastRecipeSnippet}\n\n`
      : "";

    const PROMPT_TEMPLATE = `
Du bist ein Foodblog-Autor mit einem sympathischen, modernen Schreibstil.
Deine Rezepte sind alltagstauglich, lecker, gut strukturiert und motivierend geschrieben.

WICHTIGE REGELN:
- Schreibe auf Deutsch
- Vermeide Roboter- oder Kochbuch-Sprache
- Halte den gesamten Text unter 250 Wörtern
- Nutze optional passende Emojis (sparsam und sinnvoll)
- Nummeriere die Zubereitungsschritte klar (1., 2., 3. …)
- Gib die Anzahl der Portionen an
- Nutze die angegebenen Zutaten vollständig
- Ergänze nur Basics (Salz, Pfeffer, Öl, Wasser, Standardgewürze)
- Wenn eine Zutat fehlt, schlage eine einfache Alternative vor
- Keine Markdown-Syntax verwenden (keine #, ###, ** etc.)
- Achte neben den Zutaten auch auf die spezifischen anfragen des Nutzers (z.B. "etwas Backen, einen Salat, etc." -> Rezept die spezifische Anfrage berücksichtigen)

FORMAT (exakt einhalten):
🍽️ Titel
⏱️ Zeit & Portionen
🛒 Zutaten
👩‍🍳 Zubereitung
🌿 Tipps (optional)
🥗 Variationen (optional)

${variationHint}

${previousSnippetBlock}
ANFRAGE DES NUTZERS:
{{USER_PROMPT}}
`.trim();

    const finalPrompt = PROMPT_TEMPLATE.replace("{{USER_PROMPT}}", userIntent);

    try {

const idToken = await getCurrentIdToken(true);

if (!idToken) {
  setRecipe("Dein Login-Token konnte nicht geladen werden. Bitte einmal ausloggen und neu einloggen.");
  setRecipeIsError(true);
  setShowRecipeOverlay(true);
  return;
}

const functionUrl = "https://generaterecipe-t2l67uvjtq-ey.a.run.app";

     const controller = new AbortController();
const timeout = setTimeout(() => controller.abort(), 90000); // ✅ 90s statt 25s

const response = await fetch(functionUrl, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${idToken}`,
  },
  body: JSON.stringify({
    prompt: finalPrompt,
    ingredients: trimmedIngredients,
  }),
  signal: controller.signal,
});

clearTimeout(timeout);

// ✅ robuster als response.json(), falls mal HTML/Plaintext kommt
const raw = await response.text();
let data = null;
try {
  data = JSON.parse(raw);
} catch (e) {
  data = { ok: false, error: raw || "Ungültige Server-Antwort" };
}

if (!response.ok || !data || !data.ok) {
  console.error("generateRecipe failed:", {
    status: response.status,
    data,
    raw,
  });

  const msg =
    response.status === 401
      ? "Dein Login ist abgelaufen. Bitte einmal ausloggen und neu einloggen."
      : data?.error || "Fehler beim Abrufen des Rezepts.";

  setRecipe(msg);
  setShowRecipeOverlay(true);
  return;
}

      const cleaned = cleanRecipe(data.text);
      const pretty = prettifyRecipeText(cleaned);

      // ✅ Snippet setzen für "Anderes Rezept"
      setLastRecipeSnippet(buildSnippet(pretty));
      setAttemptCount((prev) => (prev < 0 ? 1 : prev + 1)); // nach erstem Erfolg -> 1

      setRecipe(pretty);
      setShowRecipeOverlay(true);
    } catch (error) {
      console.error("Fehler beim Abrufen:", error);

      const isAbort = error && error.name === "AbortError";
      setRecipe(
        isAbort
          ? "Der Rezept-Server hat zu lange gebraucht. Bitte versuche es erneut."
          : "Es gab ein Problem beim Abrufen des Rezepts."
      );
      setShowRecipeOverlay(true);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * ✅ Speichern: NUR Firestore (kein localStorage)
   * - Nutzer bearbeitet hier NICHTS
   */
  const handleSaveRecipe = async () => {


  if (!myUid || !isVerified) {
    setShowLoginRequiredModal(true);
    return;
  }

  if (!recipe || !recipe.trim()) return;

  try {
    await saveRecipeData(myUid, {
  ingredients,
  content: recipe,
  title: "",
});

    if (!hideSavedPopup) setSavedPopup(true);
  } catch (error) {
    console.error("Fehler beim Speichern in Firestore:", error);
    alert("Speichern fehlgeschlagen. Bitte nochmal versuchen.");
  }
};

  const handleClosePopup = () => {
    const checkbox = document.getElementById("hide-recipe-popup");
    if (checkbox && checkbox.checked) {
      localStorage.setItem("hideRecipeSavedPopup", "true");
      setHideSavedPopup(true);
    }
    setSavedPopup(false);
  };

  // ✅ Button-Label: nach 1. Rezept automatisch "Anderes Rezept"
  const buttonLabel = attemptCount >= 1 ? "Anderes Rezept 🔁" : "An die Küche, fertig, los!";

  return (
    <div className="RezeptPage">
      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Edens Ernte Logo"
        className="browse-logo"
        onClick={() => navigate("/")}
      />

      <GateIcon className="login-icon" />

      <SpeechIcon className="speech-icon no-tap-highlight" />

      <img
        src="/rezepte-icon.png"
        alt="Rezepte"
        className="rezepte-icon"
        onClick={handleMeineRezepteClick}
      />

      <p className="top-claim">Kochen mit Kreativität!</p>

      <div className="rezept-content">
        <h2>Dein Küchenhelfer</h2>
        <p className="rezept-subline">
          Sag mir was du für Zutaten gerade da hast – ich zauber ein Rezept daraus!
          <br />
          Du hast Lust auf etwas Bestimmtes? Dann habe ich auch ein tolles Rezept für dich!
          <br />
          Ob gebacken, gekocht oder Salat – zusammen machen wir etwas Tolles!
          <br />
          Sag mir gerne auch für wie viele Personen du kochen möchtest. 😊
        </p>

        <textarea
          className="rezept-input"
          placeholder="Welche Zutaten hast du?"
          value={ingredients}
          onChange={(e) => {
            setIngredients(e.target.value);

            // ✅ Wenn Zutaten geändert werden, ist "Anderes Rezept" nicht mehr sinnvoll
            // -> Reset der Retry-Logik (nur RAM)
            setAttemptCount(0);
            setLastRecipeSnippet("");
          }}
        />

        <button
          className="rezept-green-button"
          onClick={fetchRecipe}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="spinner-inline"></span> Bitte warten …
            </>
          ) : (
            buttonLabel
          )}
        </button>

        <p className="rezept-subline2">
          Gib mir einen kurzen Moment – dein Rezept kommt sofort!
        </p>
      </div>

      {savedPopup && (
        <div className="info-popup-overlay">
          <div className="info-popup-container">
            <p className="info-popup-text">
              Das Rezept wurde jetzt auf deiner <strong>Rezept-Seite</strong> (oben rechts)
              gespeichert. Du kannst es dort umbenennen und bearbeiten.
            </p>
            <div className="info-popup-actions">
              <label className="info-popup-checkbox">
                <input type="checkbox" id="hide-recipe-popup" />
                Diese Nachricht nicht mehr anzeigen
              </label>
              <button className="info-popup-button" onClick={handleClosePopup}>
                Verstanden
              </button>
            </div>
          </div>
        </div>
      )}

      {showRecipeOverlay && (
        <div className="recipe-overlay" onClick={() => setShowRecipeOverlay(false)}>
          <div className="recipe-popup" onClick={(e) => e.stopPropagation()}>
            <pre className="recipe-output">{recipe}</pre>

            <div className="recipe-buttons">
              {recipe && !recipe.toLowerCase().includes("problem beim abrufen") && (
            <button className="green-button save-button" onClick={handleSaveRecipe}>
                Rezept speichern
             </button>
  )}
              <button
                className="red-button close-button"
                onClick={() => setShowRecipeOverlay(false)}
              >
                ✖ Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔐 Login erforderlich */}
      {showLoginRequiredModal && (
        <div className="modal-overlay" onClick={() => setShowLoginRequiredModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Login erforderlich</h3>
            <p>
              Um den Rezepthelfer und deine Rezeptübersicht zu nutzen, musst du eingeloggt und deine E-Mail-Adresse bestätigt sein.
            </p>
            <div className="modal-buttons">
              <button
                className="green-button"
                onClick={() => {
                  setShowLoginRequiredModal(false);
                  navigate("/login");
                }}
              >
                Zum Login
              </button>
              <button
                className="red-button"
                onClick={() => setShowLoginRequiredModal(false)}
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RezeptPage;